<template>
  <div class="panel cc-panel">
    <p class="panel-heading">
      {{ $t("i18n.chartTitles.ccUsedAndGenerated") }}
    </p>
    <div class="panel-block">
      <div :id="this.idChartDiv" class="cc-chart" :style="chartDivInlineStyle"></div>
    </div>
  </div>
</template>
<script>
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting'
import ccColors from '@/components/ccColors'

export default {
  name: 'ccUsedAndGenerated',
  props: {
    idChartDiv: {
      type: String,
      required: true
    },
    totals: {
      type: Object,
      required: true
    },
    unitApplicationTotals: {
      type: Array,
      required: true
    },
    chartDivInlineStyle: {
      type: String,
      required: true
    }
  },
  data () {
    return {}
  },
  computed: {
    compressorTitle: function () {
      return this.$t('i18n.global.compressor')
    },
    auxiliaryTitle: function () {
      return this.$t('i18n.global.auxiliary')
    },
    totalsTitle: function () {
      return this.$t('i18n.global.total')
    },
    signatureTitle: function () {
      return this.$t('i18n.global.signature')
    }
  },
  mounted () {
    this.buildChart()
  },
  methods: {
    buildChart: function () {
      am5.addLicense(this.$store.state.am5)
      const root = am5.Root.new(this.idChartDiv)

      root.numberFormatter.setAll({
        numberFormat: '#.'
      })

      // Create chart
      // https://www.amcharts.com/docs/v5/charts/xy-chart/
      const chart = root.container.children.push(am5xy.XYChart.new(root, {
        interactive: false,
        layout: root.verticalLayout,
        paddingRight: 65,
        paddingTop: 0
      }))

      const electricalData = [{
        category: this.$t('i18n.global.electrical'),
        compressor: this.totals.compressor,
        auxiliary: this.totals.auxiliary,
        signature: this.totals.signature
      }]

      const coolingData = []
      const heatingData = []
      let coolingTitle = this.$t('i18n.global.cooling')
      let heatingTitle = this.$t('i18n.global.heating')

      // If there are multiple types of units (unit.application), show heating and cooling per unit
      if (this.unitApplicationTotals.length <= 1) {
        coolingData.push({
          category: coolingTitle,
          cooling: this.totals.cooling
        })
        heatingData.push({
          category: heatingTitle,
          heating: this.totals.heating
        })
      } else {
        this.unitApplicationTotals.forEach(totalsPerApplication => {
          coolingTitle = this.$t('i18n.global.cooling') + ' ' + this.$t('i18n.unitApplications.' + totalsPerApplication.application)
          heatingTitle = this.$t('i18n.global.heating') + ' ' + this.$t('i18n.unitApplications.' + totalsPerApplication.application)
          coolingData.push({
            category: coolingTitle,
            [totalsPerApplication.application]: totalsPerApplication.cooling
          })
          heatingData.push({
            category: heatingTitle,
            [totalsPerApplication.application]: totalsPerApplication.heating
          })
        })
      }

      // Create axes
      // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
      const yAxis = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'category',
          startLocation: 0,
          renderer: am5xy.AxisRendererY.new(root, {})
        })
      )

      yAxis.data.setAll([...electricalData, ...coolingData, ...heatingData])

      const xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          renderer: am5xy.AxisRendererX.new(root, {})
        })
      )

      if (this.unitApplicationTotals.length <= 1) {
        this.makeSeries(this.$t('i18n.global.heating'), heatingTitle, 'heating', ccColors.heating, heatingData, yAxis, xAxis, root, chart, 0.5, false, false)
        this.makeSeries(this.$t('i18n.global.cooling'), coolingTitle, 'cooling', ccColors.cooling, coolingData, yAxis, xAxis, root, chart, 0.5, false, false)
      } else {
        this.unitApplicationTotals.forEach((applicationTypeCoolingData, index) => {
          // const stacked = index !== 0
          this.makeSeries(this.$t('i18n.global.cooling') + ' ' + this.$t('i18n.unitApplications.' + applicationTypeCoolingData.application), coolingTitle, applicationTypeCoolingData.application, ccColors.cold[index], coolingData, yAxis, xAxis, root, chart, 0.5, false, false)
        })
        this.unitApplicationTotals.forEach((applicationTypeHeatingData, index) => {
          // const stacked = index !== 0
          this.makeSeries(this.$t('i18n.global.heating') + ' ' + this.$t('i18n.unitApplications.' + applicationTypeHeatingData.application), heatingTitle, applicationTypeHeatingData.application, ccColors.warm[index], heatingData, yAxis, xAxis, root, chart, 0.5, false, false)
        })
      }
      this.makeSeries(this.$t('i18n.global.electrical'), this.$t('i18n.global.compressor'), 'compressor', ccColors.energyComp, electricalData, yAxis, xAxis, root, chart, 0.5, false, false)
      this.makeSeries(this.$t('i18n.global.electrical'), this.$t('i18n.global.auxiliary'), 'auxiliary', ccColors.energyAux, electricalData, yAxis, xAxis, root, chart, 0.5, false, true)

      this.makeStepLineSeries(this.$t('i18n.global.signature'), 'signature', ccColors.signature, electricalData, yAxis, xAxis, root, chart)

      // Add export menu
      am5exporting.Exporting.new(root, {
        menu: am5exporting.ExportingMenu.new(root, {})
      })

      // Add Label to Energy axis
      chart.children.unshift(am5.Label.new(root, {
        text: 'kWh',
        fontWeight: '500',
        textAlign: 'left',
        x: am5.percent(100),
        y: am5.percent(90),
        paddingLeft: 30
      }))

      // Make stuff animate on load
      // https://www.amcharts.com/docs/v5/concepts/animations/
      chart.appear(1000, 100)
    },
    makeSeries: function (name, title, fieldName, color, data, yAxis, xAxis, root, chart, bulletOffset, clustered, stacked) {
      // Add series
      // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
      const series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        baseAxis: yAxis,
        valueXField: fieldName,
        categoryYField: 'category',
        stacked: stacked,
        legendLabelText: '{name}',
        clustered: clustered
      }))

      series.columns.template.setAll({
        strokeWidth: 0,
        stroke: 0,
        fill: color,
        height: am5.percent(90)
      })
      // place total at end of column
      // handle specials for electrical where we place on bullet for both stacks on the rightmost one
      // compressor allways first (left)
      // auxiliary allways second (right)
      let auxOrCompColumnBullet = 'auxiliary'

      // if no aux, bullet on compressor
      if (this.totals.aux === 0) {
        auxOrCompColumnBullet = 'compressor'
      }

      if (fieldName === auxOrCompColumnBullet) {
        const bulletText = this.compressorTitle + ': ' + this.totals.compressor + ' kWh | ' +
          this.totalsTitle + ': ' + (this.totals.compressor + this.totals.auxiliary) + ' kWh\n' +
          this.auxiliaryTitle + ': ' + this.totals.auxiliary + ' kWh | ' +
          this.signatureTitle + ': ' + this.totals.signature + ' kWh'

        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationX: 1,
            locationY: bulletOffset,
            sprite: am5.Label.new(root, {
              centerX: am5.p100,
              centerY: am5.p50,
              text: bulletText,
              fill: 'black',
              populateText: true
            })
          })
        })
      } else if (['auxiliary', 'compressor'].indexOf(fieldName) === -1) {
        // add bullets for heating/cooling
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationX: 1,
            locationY: bulletOffset,
            sprite: am5.Label.new(root, {
              centerX: am5.p100,
              centerY: am5.p50,
              text: '{valueX} kWh',
              fill: 'black',
              populateText: true
            })
          })
        })
      }

      // realocate bullet if column is to narrow
      // Places bullet outside of column
      series.columns.template.onPrivate('width', function (width, target) {
        // dont do this unless the series has bullet
        if (target.dataItem.bullets) {
          am5.array.each(target.dataItem.bullets, function (bullet) {
            if (width < 150) {
              bullet.set('locationX', 1)
              bullet.get('sprite').set('centerX', am5.percent(0))
            }
          })
        }
      })

      series.data.setAll(data)
    },
    makeStepLineSeries: function (name, fieldName, color, data, yAxis, xAxis, root, chart) {
      // Add series
      // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
      const series = chart.series.push(am5xy.StepLineSeries.new(root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        baseAxis: yAxis,
        valueXField: fieldName,
        categoryYField: 'category',
        legendLabelText: '{name}',
        noRisers: true,
        stroke: color
      }))

      series.strokes.template.setAll({
        strokeWidth: 3,
        strokeDasharray: [2, 2]
      })

      series.data.setAll(data)
    }
  }
}
</script>
